<script lang="ts">
  import { getContext } from 'svelte';
  import { Link } from '@squareup/public-web-design-system';
  import VideoPlayIconPartial from '$components/common/partials/VideoPlayPartial/VideoPlayIconPartial.svelte';
  import VideoTriggerPartial from '$components/common/partials/VideoTriggerPartial/VideoTriggerPartial.svelte';
  import { VideoUrlDetector } from '$components/common/partials/VideoTriggerPartial/video-url-detector';
  import { DesignSystem as DesignSystemEnum } from '$lib/helpers/design-system';
  import type { LinkComponentVideoProps } from './types';

  export let size: LinkComponentVideoProps['size'];
  export let nofollow: LinkComponentVideoProps['nofollow'];
  export let target: LinkComponentVideoProps['target'];
  export let text: LinkComponentVideoProps['text'];
  export let url: LinkComponentVideoProps['url'];
  export let videoPlayButtonSize: LinkComponentVideoProps['videoPlayButtonSize'];
  export let videoPlayButtonTitle: LinkComponentVideoProps['videoPlayButtonTitle'];
  export let doArrowWrap: LinkComponentVideoProps['doArrowWrap'];
  export let balanceText: LinkComponentVideoProps['balanceText'];

  const designSystem = getContext<DesignSystemEnum>('designSystem');
</script>

{#if VideoUrlDetector.isSupported(url)}
  {#if designSystem === DesignSystemEnum.PublicWebDesignSystem}
    <VideoTriggerPartial videoUrl={url} let:handleVideoClick>
      <Link
        variant="video"
        {size}
        href={url}
        target={target ?? undefined}
        rel={nofollow ?? undefined}
        balanced={balanceText}
        {text}
        preventOrphanIcon={doArrowWrap}
        onClick={handleVideoClick}
      />
    </VideoTriggerPartial>
  {:else}
    <VideoTriggerPartial videoUrl={url}>
      <div class="LinkComponent--video" title={videoPlayButtonTitle} slot="video-player-link-content">
        <div class="iconAndTextContainer">
          <div class="iconContainer">
            <VideoPlayIconPartial iconSize={videoPlayButtonSize} />
          </div>
          <div class="videoTextContainer">{text}</div>
        </div>
      </div>
    </VideoTriggerPartial>
  {/if}
{:else if designSystem === DesignSystemEnum.PublicWebDesignSystem}
  <Link
    variant="video"
    href={url}
    target={target ?? undefined}
    rel={nofollow ?? undefined}
    balanced={balanceText}
    {text}
    preventOrphanIcon={doArrowWrap}
  />
{:else}
  <a class="LinkComponent--video" href={url} title={videoPlayButtonTitle} {target} rel={nofollow}>
    <div class="iconAndTextContainer">
      <div class="iconContainer">
        <VideoPlayIconPartial iconSize={videoPlayButtonSize} />
      </div>
      <div class="videoTextContainer">{text}</div>
    </div>
  </a>
{/if}

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/mixins/grid.scss' as grid;

  .LinkComponent--video {
    display: inline-block;
    vertical-align: middle;

    font-family: var(--font-family, inherit);
    font-size: var(--font-size, var(--font-size-button));
    line-height: var(--line-height, var(--line-height-button));
    color: var(--color-link, var(--color-blue));
    font-weight: var(--font-weight-link, var(--font-weight-medium));
    text-decoration: none;

    &:hover,
    &:focus {
      transition: color var(--duration-quick) var(--easing-sharp);
      color: var(--color-link-hover, var(--color-blue-dark));

      :global(.iconPlay) {
        background-color: var(--color-blue-dark);
      }
    }
  }

  .iconAndTextContainer {
    @include grid.flex;
    @include grid.space(10);
    @include grid.valign('middle');
  }

  .iconContainer {
    flex-shrink: 0;
  }
</style>
